import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { useLocalization } from '@/shared/i18n'
import type { TranslationKey } from '@/shared/i18n'
import { Button, ErrorState, Progress, Stack, Stage, Text } from '@/shared/ui'

const CHECKPOINTS: TranslationKey[] = [
  'onboarding.building.profile',
  'onboarding.building.rules',
  'onboarding.building.deadlines',
  'onboarding.building.tasks',
]

const CHECKPOINT_MS = 600

type Mark = 'done' | 'now' | 'todo'

function markOf(index: number, reached: number): Mark {
  if (index < reached) {
    return 'done'
  }

  return index === reached ? 'now' : 'todo'
}

export function Building({
  rail,
  country,
  ready,
  failure,
  onRetry,
  onDone,
}: {
  rail: ReactNode
  country: string
  ready: boolean
  failure: unknown
  onRetry: () => void
  onDone: () => void
}) {
  const { t } = useLocalization()
  const [reached, setReached] = useState(0)

  const last = CHECKPOINTS.length - 1
  const finished = reached > last

  useEffect(() => {
    if (failure || (reached === last && !ready)) {
      return
    }

    const timer = window.setTimeout(
      () => (finished ? onDone() : setReached(reached + 1)),
      CHECKPOINT_MS,
    )

    return () => window.clearTimeout(timer)
  }, [failure, finished, last, onDone, reached, ready])

  if (failure) {
    return (
      <Stage rail={rail}>
        <Stack gap="sm" align="start">
          <Text as="h1" role="heading-2">
            {t('onboarding.building.failed')}
          </Text>
          <Text tone="muted" measure>
            {t('onboarding.building.kept')}
          </Text>
        </Stack>

        <ErrorState error={failure} onRetry={onRetry} />
      </Stage>
    )
  }

  return (
    <Stage
      rail={rail}
      footer={
        <Button full variant="quiet" disabled>
          {t('onboarding.building.wait')}
        </Button>
      }
    >
      <Stack gap="lg">
        <Stack gap="sm" align="start">
          <Text as="h1" role="heading-2">
            {t('onboarding.building.title')}
          </Text>
          <Text role="caption" tone="muted" measure>
            {t('onboarding.building.body', { country })}
          </Text>
        </Stack>

        <div className="building__meter">
          <Progress
            value={finished ? CHECKPOINTS.length : reached + 0.5}
            max={CHECKPOINTS.length}
            label={t('onboarding.building.title')}
          />
        </div>

        <Stack as="ol" gap="md" className="building__checkpoints">
          {CHECKPOINTS.map((key, index) => {
            const mark = markOf(index, reached)

            return (
              <Stack as="li" key={key} direction="row" gap="sm">
                <span className={`building__mark building__mark--${mark}`} aria-hidden="true" />
                <Text as="span" role="body-strong" tone={mark === 'todo' ? 'muted' : 'default'}>
                  {t(key, { country })}
                </Text>
              </Stack>
            )
          })}
        </Stack>
      </Stack>
    </Stage>
  )
}
